import { useState, useMemo, useEffect } from "react";
import SEO from "@/components/feature/SEO";
import AdminLayout from "@/components/feature/AdminLayout";
import { useAdminData, getAutoAssignAdvisor } from "@/hooks/useAdmin";
import {
  Search,
  Filter,
  ArrowUpDown,
  Phone,
  Mail,
  MapPin,
  Calendar,
  CheckCircle,
  XCircle,
  MoreHorizontal,
  Download,
  UserCircle,
  Sparkles,
  Radio,
  Bell,
  X,
} from "lucide-react";

const statusOptions = ["all", "new", "contacted", "qualified", "converted", "lost"] as const;

const advisorOptions = [
  "Amit Patel",
  "Rahul Mehta",
  "Priya Sharma",
  "Kavita Joshi",
  "Deepak Sinha",
];

type SortKey = "created_at" | "name" | "city" | "status";

export default function AdminLeadsPage() {
  const {
    leads,
    updateLeadStatus,
    assignLead,
    autoAssignLead,
    loading,
    error,
    realtimeConnected,
    newLeadIds,
    dismissNewLead,
  } = useAdminData();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [assignedFilter, setAssignedFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [assignMenuOpen, setAssignMenuOpen] = useState<string | null>(null);
  const [toasts, setToasts] = useState<{ id: string; name: string }[]>([]);

  // Show toast when new leads arrive
  useEffect(() => {
    if (newLeadIds.length === 0) return;
    const fresh = newLeadIds
      .map((id) => leads.find((l) => l.id === id))
      .filter(Boolean);
    if (fresh.length === 0) return;
    setToasts((prev) => {
      const existing = new Set(prev.map((t) => t.id));
      const incoming = fresh
        .filter((l) => !existing.has(l!.id))
        .map((l) => ({ id: l!.id, name: l!.name }));
      return [...incoming.slice(-3), ...prev].slice(0, 5);
    });
  }, [newLeadIds, leads]);

  // Auto-dismiss toasts
  useEffect(() => {
    if (toasts.length === 0) return;
    const timer = setTimeout(() => setToasts((prev) => prev.slice(1)), 4000);
    return () => clearTimeout(timer);
  }, [toasts]);

  const sourceOptions = useMemo(() => {
    const sources = new Set<string>();
    leads.forEach((l) => {
      if (l.source) sources.add(l.source);
    });
    return ["all", ...Array.from(sources).sort()];
  }, [leads]);

  const filtered = leads
    .filter((l) => {
      const matchesSearch =
        search === "" ||
        l.name.toLowerCase().includes(search.toLowerCase()) ||
        l.email.toLowerCase().includes(search.toLowerCase()) ||
        l.phone.includes(search) ||
        l.city.toLowerCase().includes(search.toLowerCase()) ||
        (l.assigned_to && l.assigned_to.toLowerCase().includes(search.toLowerCase())) ||
        (l.source && l.source.toLowerCase().includes(search.toLowerCase()));
      const matchesStatus = statusFilter === "all" || l.status === statusFilter;
      const matchesSource = sourceFilter === "all" || l.source === sourceFilter;
      const matchesAssigned =
        assignedFilter === "all" ||
        (assignedFilter === "unassigned" ? !l.assigned_to : l.assigned_to === assignedFilter);
      return matchesSearch && matchesStatus && matchesSource && matchesAssigned;
    })
    .sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortKey === "created_at")
        return (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) * dir;
      return String(a[sortKey]).localeCompare(String(b[sortKey])) * dir;
    });

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const statusBadge: Record<string, string> = {
    new: "bg-amber-100 text-amber-700",
    contacted: "bg-blue-100 text-blue-700",
    qualified: "bg-purple-100 text-purple-700",
    converted: "bg-emerald-100 text-emerald-700",
    lost: "bg-red-100 text-red-700",
  };

  const exportToCSV = () => {
    const headers = [
      "ID", "Name", "Email", "Phone", "City", "Insurance Type", "Source", "Status", "Assigned To", "Notes", "Created At"
    ];
    const rows = filtered.map((lead) => [
      lead.id,
      lead.name,
      lead.email,
      lead.phone,
      lead.city,
      lead.insurance_type,
      lead.source,
      lead.status,
      lead.assigned_to || "",
      lead.notes || "",
      new Date(lead.created_at).toLocaleString("en-IN"),
    ]);
    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `leads-${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <SEO title="Lead Management | ExpertAdvise.in Admin" noindex />
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        {/* Toasts */}
        <div className="fixed top-4 right-4 z-50 space-y-2 pointer-events-none">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className="pointer-events-auto bg-emerald-600 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-3 animate-[slideIn_0.3s_ease-out] min-w-[280px]"
            >
              <Bell className="w-4 h-4 shrink-0" />
              <div className="flex-1 text-sm">
                <span className="font-semibold">New Lead!</span>
                <div className="text-white/90">{toast.name} just submitted a quote request</div>
              </div>
              <button
                onClick={() => {
                  setToasts((prev) => prev.filter((t) => t.id !== toast.id));
                  dismissNewLead(toast.id);
                }}
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">Lead Management</h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage and track all incoming leads. {filtered.length} of {leads.length} leads shown.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-slate-100 text-slate-600">
              <Radio className={`w-3.5 h-3.5 ${realtimeConnected ? "text-emerald-500" : "text-slate-400"}`} />
              <span className={`w-2 h-2 rounded-full ${realtimeConnected ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
              {realtimeConnected ? "Realtime Live" : "Realtime Off"}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
            Failed to load leads: {error}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4 md:mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search leads..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 outline-none focus:border-primary-400 placeholder:text-slate-400"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:border-primary-400 cursor-pointer"
          >
            <option value="all">All Status</option>
            {statusOptions
              .filter((s) => s !== "all")
              .map((s) => (
                <option key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:border-primary-400 cursor-pointer"
          >
            <option value="all">All Sources</option>
            {sourceOptions
              .filter((s) => s !== "all")
              .map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
          </select>
          <select
            value={assignedFilter}
            onChange={(e) => setAssignedFilter(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:border-primary-400 cursor-pointer"
          >
            <option value="all">All Assignments</option>
            <option value="unassigned">Unassigned</option>
            {advisorOptions.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <button
            onClick={exportToCSV}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500 text-sm">
            Loading leads...
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {[
                      { key: "name" as SortKey, label: "Lead" },
                      { key: "city" as SortKey, label: "City / Type" },
                      { key: "status" as SortKey, label: "Status" },
                      { key: "created_at" as SortKey, label: "Date" },
                    ].map((col) => (
                      <th
                        key={col.key}
                        className="text-left font-semibold text-slate-700 px-4 py-3 cursor-pointer select-none whitespace-nowrap"
                        onClick={() => toggleSort(col.key)}
                      >
                        <div className="flex items-center gap-1">
                          {col.label}
                          <ArrowUpDown className="w-3 h-3 text-slate-400" />
                        </div>
                      </th>
                    ))}
                    <th className="text-left font-semibold text-slate-700 px-4 py-3 whitespace-nowrap">Source</th>
                    <th className="text-left font-semibold text-slate-700 px-4 py-3 whitespace-nowrap">Assigned</th>
                    <th className="w-10 px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((lead) => {
                    const isNewlyArrived = newLeadIds.includes(lead.id);
                    return (
                      <tr
                        key={lead.id}
                        className={`hover:bg-slate-50 transition-colors ${
                          isNewlyArrived ? "bg-emerald-50/60 animate-[pulseHighlight_2s_ease-in-out]" : ""
                        }`}
                      >
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-slate-900">{lead.name}</div>
                            {isNewlyArrived && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                New
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {lead.email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {lead.phone}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1 text-slate-700">
                            <MapPin className="w-3.5 h-3.5 text-slate-400" />
                            {lead.city}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">{lead.insurance_type}</div>
                        </td>
                        <td className="px-4 py-3.5">
                          <span
                            className={`inline-flex text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize ${
                              statusBadge[lead.status] || "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {lead.status}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-slate-600 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                            {new Date(lead.created_at).toLocaleDateString("en-IN")}
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-slate-600">{lead.source}</td>
                        <td className="px-4 py-3.5">
                          {lead.assigned_to ? (
                            <div className="flex items-center gap-1.5">
                              <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center">
                                <UserCircle className="w-3 h-3 text-teal-600" />
                              </div>
                              <span className="text-xs text-slate-700 font-medium">{lead.assigned_to}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs text-slate-400 italic">Unassigned</span>
                              <button
                                onClick={() => {
                                  autoAssignLead(lead.id, lead.insurance_type);
                                  setActionMenuOpen(null);
                                }}
                                className="flex items-center gap-1 text-[11px] text-teal-600 hover:text-teal-700 font-medium"
                                title="Auto-assign based on insurance type"
                              >
                                <Sparkles className="w-3 h-3" />
                                Auto
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3.5 relative">
                          <button
                            onClick={() =>
                              setActionMenuOpen(actionMenuOpen === lead.id ? null : lead.id)
                            }
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                          {actionMenuOpen === lead.id && (
                            <div className="absolute right-2 top-full mt-1 w-48 bg-white rounded-lg border border-slate-200 shadow-lg z-20 py-1">
                              {statusOptions
                                .filter((s) => s !== "all" && s !== lead.status)
                                .map((s) => (
                                  <button
                                    key={s}
                                    onClick={() => {
                                      updateLeadStatus(lead.id, s);
                                      setActionMenuOpen(null);
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                  >
                                    {s === "converted" ? (
                                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                                    ) : s === "lost" ? (
                                      <XCircle className="w-3.5 h-3.5 text-red-500" />
                                    ) : null}
                                    Mark {s}
                                  </button>
                                ))}
                              <div className="border-t border-slate-100 my-1" />
                              <button
                                onClick={() => {
                                  setAssignMenuOpen(assignMenuOpen === lead.id ? null : lead.id);
                                }}
                                className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                              >
                                <UserCircle className="w-3.5 h-3.5 text-slate-500" />
                                Assign To...
                              </button>
                              {!lead.assigned_to && (
                                <button
                                  onClick={() => {
                                    autoAssignLead(lead.id, lead.insurance_type);
                                    setActionMenuOpen(null);
                                  }}
                                  className="w-full text-left px-3 py-1.5 text-sm text-teal-700 hover:bg-teal-50 flex items-center gap-2"
                                >
                                  <Sparkles className="w-3.5 h-3.5" />
                                  Auto Assign
                                </button>
                              )}
                            </div>
                          )}
                          {assignMenuOpen === lead.id && (
                            <div className="absolute right-2 top-full mt-1 w-44 bg-white rounded-lg border border-slate-200 shadow-lg z-30 py-1">
                              {advisorOptions.map((advisor) => (
                                <button
                                  key={advisor}
                                  onClick={() => {
                                    assignLead(lead.id, advisor);
                                    setAssignMenuOpen(null);
                                    setActionMenuOpen(null);
                                  }}
                                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${
                                    lead.assigned_to === advisor
                                      ? "text-teal-700 bg-teal-50 font-medium"
                                      : "text-slate-700 hover:bg-slate-50"
                                  }`}
                                >
                                  <UserCircle className="w-3.5 h-3.5" />
                                  {advisor}
                                </button>
                              ))}
                              <div className="border-t border-slate-100 my-1" />
                              <button
                                onClick={() => {
                                  assignLead(lead.id, null);
                                  setAssignMenuOpen(null);
                                  setActionMenuOpen(null);
                                }}
                                className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                              >
                                <XCircle className="w-3.5 h-3.5" />
                                Unassign
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && (
              <div className="text-center py-12 text-slate-500 text-sm">
                No leads match your filters.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Animation keyframes injected via style tag */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes pulseHighlight {
          0%, 100% { background-color: rgba(240,253,244,0.6); }
          50% { background-color: rgba(187,247,208,0.5); }
        }
      `}</style>
    </AdminLayout>
  );
}